import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { DEFAULT_PAGE_SIZE, pascalToCamel, urlEndPoint } from "helpers/GeneralHelper";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import { AsyncClientSelect, AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import {
  applyClientAsyncFilterOptionToFormState,
  applyUserAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  buildUserAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
  getUserAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  mapFilterDefinitionsToModalData,
  mergeModalSubmitIntoListFilter,
} from "helpers/metadataFilterHelpers";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useClientReviewContext } from "libs/context/ClientReviewContext";
import { submitClientReviewFilterData } from "model/ClientReviewModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const normalizeClientReviewFilterPayload = (raw = {}) => {
  const out = { ...raw };
  ["nameContains", "clientId", "userId", "fromDate", "toDate"].forEach((key) => {
    if (key in out) out[key] = strOrNull(out[key]);
  });
  Object.keys(out).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) delete out[key];
  });
  return out;
};

const ClientReview = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchClientReviewContextData, clientReviewContextData } = useClientReviewContext() || {};
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("ClientReview");
  const initialFilterFetchedRef = useRef(false);
    const [modal1, setModal1] = useState(false);
  const [filterForm, setFilterForm] = useState({
    search: "",
    clientId: "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    userId: "",
    [getUserAsyncFilterLabelFieldKey()]: "",
    fromDate: "",
    toDate: "",
  });
  const [clientReviewData, setClientReviewData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    nameContains: null,
    clientId: null,
    userId: null,
    fromDate: null,
    toDate: null,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

    const toggleViewModal = () => setModal1(!modal1);

  const filteredData = useMemo(() => clientReviewData, [clientReviewData]);

  const clientReviewActionPermissions = useMemo(
    () => ({
      canEdit: Boolean(permissionData?.ClientReviewUpdateAccess),
    }),
    [permissionData]
  );

  const hasAnyClientReviewRowAction = useMemo(
    () => clientReviewActionPermissions.canEdit,
    [clientReviewActionPermissions]
  );

  const clientReviewFilterModalData = useMemo(
    () =>
      mapFilterDefinitionsToModalData(
        clientReviewContextData?.filters || [],
        filterForm
      ),
    [clientReviewContextData?.filters, filterForm]
  );

  useEffect(() => {
    fetchClientReviewContextData?.();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getClientReviewList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const valueReModified = clientReviewContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: pascalToCamel(col?.field),
      type: col?.type,
    }));
    setTableMetaData(valueReModified || []);
  }, [clientReviewContextData]);

  const getClientReviewList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = normalizeClientReviewFilterPayload({
      pageNumber: requestPayload.pageNumber,
      pageSize: requestPayload.pageSize,
      nameContains: requestPayload.nameContains,
      clientId: requestPayload.clientId,
      userId: requestPayload.userId,
      fromDate: requestPayload.fromDate,
      toDate: requestPayload.toDate,
      ...payloadOverrides,
    });

    delete payload.search;
    if (payload?.user && !payload?.userId) {
      payload.userId = payload.user;
    }
    delete payload.user;

    const response = await submitClientReviewFilterData(payload);
    const responseData = response?.data || {};
    const listData = responseData?.records || responseData || [];

    const valueReModified = (Array.isArray(listData) ? listData : []).map((item = {}) => ({
      ...item,
      id: item?.id || item?._id || "",
    }));

    if (response) {
      setClientReviewData(valueReModified || []);
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || 0,
      });
      setRequestPayload(normalizeClientReviewFilterPayload(payload));
    } else {
      setClientReviewData([]);
    }
    setLoading(false);
  };

  const getClientReviewEditHref = (row = {}) => {
    const clientId = row?.clientId;
    return clientId ? `/${urlEndPoint.clientReviewEdit}/${clientId}` : null;
  };

  const handleFilterSubmit = async (modalFormPayload = {}) => {
    const mergedFilterForm = mergeModalSubmitIntoListFilter(
      filterForm,
      clientReviewFilterModalData,
      modalFormPayload
    );
    setFilterForm(mergedFilterForm);
    await getClientReviewList({
      ...buildPayloadFromUpperForm(mergedFilterForm),
      pageNumber: 1,
    });
    setCommonFilterModalOpen(false);
  };

  const buildPayloadFromUpperForm = (form = {}) =>
    normalizeClientReviewFilterPayload({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      nameContains: strOrNull(form?.search),
      clientId: strOrNull(form?.clientId),
      userId: strOrNull(form?.userId),
      fromDate: strOrNull(form?.fromDate),
      toDate: strOrNull(form?.toDate),
    });

  const handleUpperSearch = () => {
    getClientReviewList(buildPayloadFromUpperForm(filterForm));
  };

  const handleFilterFieldChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePageChange = async (page) => {
    await getClientReviewList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getClientReviewList({ pageNumber: 1, pageSize: rows });
  };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

          <Card>
                        <CardBody>
              <h5 className="fw-bold mb-3">Client Review</h5>
              <hr />

                            <LegacyFilterToolbar>
              <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--text">
                  <span className="manage-user-filter-label">Search</span>
                  <div className="metadata-filter-field__control">
                                        <Input
                                            type="text"
                    name="search"
                    placeholder="Search by Email/Company etc."
                    className={LEGACY_FILTER_INPUT_CLASS}
                    value={filterForm.search}
                    onChange={handleFilterFieldChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleUpperSearch();
                      }
                    }}
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                  <span className="manage-user-filter-label">Client</span>
                  <div className="metadata-filter-field__control">
                  <AsyncClientSelect
                    key={`client-review-client-${filterForm.clientId}`}
                    pageName="ClientReview"
                    value={filterForm.clientId}
                    initialOption={buildClientAsyncFilterInitialOption(
                      filterForm.clientId,
                      filterForm[getClientAsyncFilterLabelFieldKey()]
                    )}
                    onChange={(option) =>
                      setFilterForm((prev) => applyClientAsyncFilterOptionToFormState(prev, option))
                    }
                    placeholder="Select Client"
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                  <span className="manage-user-filter-label">User</span>
                  <div className="metadata-filter-field__control">
                  <AsyncUserSelect
                    key={`client-review-user-${filterForm.userId}`}
                    pageName="ClientReview"
                    value={filterForm.userId}
                    initialOption={buildUserAsyncFilterInitialOption(
                      filterForm.userId,
                      filterForm[getUserAsyncFilterLabelFieldKey()]
                    )}
                    onChange={(option) =>
                      setFilterForm((prev) => applyUserAsyncFilterOptionToFormState(prev, option))
                    }
                    placeholder="Select User"
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                  <span className="manage-user-filter-label">From date</span>
                  <div className="metadata-filter-field__control">
                  <Input
                    type="date"
                    name="fromDate"
                    value={filterForm.fromDate}
                    onChange={handleFilterFieldChange}
                    className={LEGACY_FILTER_INPUT_CLASS}
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                  <span className="manage-user-filter-label">To date</span>
                  <div className="metadata-filter-field__control">
                  <Input
                    type="date"
                    name="toDate"
                    value={filterForm.toDate}
                    onChange={handleFilterFieldChange}
                    className={LEGACY_FILTER_INPUT_CLASS}
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                  <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                  <div className="metadata-filter-toolbar__actions">
                                        <Button
                    color="primary"
                    className="w-100 manage-user-search-btn"
                    onClick={handleUpperSearch}
                  >
                    <i className="fa fa-search me-1"></i>
                    Search
                                        </Button>
                  </div>
                </Col>
              </Row>
              </LegacyFilterToolbar>

              {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyClientReviewRowAction}
                  getEditHref={
                    clientReviewActionPermissions.canEdit
                      ? getClientReviewEditHref
                      : undefined
                  }
                  isEditAccess={clientReviewActionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"ClientReview"}
                />
              ) : (
                // <Card className="border mb-3">
                //   <CardBody>
                //     <Spinners setLoading={setLoading} />
                //         </CardBody>
                //     </Card>
                                <Loaders />
                
              )}
                        </CardBody>
                    </Card>

                    <AllCommonFilterModal
                setCommonFilterModalOpen={setCommonFilterModalOpen}
                commonFilterModalOpen={commonFilterModalOpen}
                modalTitle="Client Review"
            handleSubmit={handleFilterSubmit}
            filterData={clientReviewFilterModalData}
            />
                </Container>
            </div>
        </React.Fragment>
    );
};

ClientReview.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(ClientReview);
